import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Vancouver = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.vancouver:About Vancouver Paragraph">
        <Paragraph>
          The city of Vancouver is home to some of the liveliest most diverse
          communities in{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          . From heritage low and mid-rise apartment buildings in the West End
          to Yaletown’s converted warehouses and Coal Harbour’s glitzy condo
          towers, Vancouver boasts an eclectic mix of housing options. With
          great amenities, a great transit system, and thriving businesses, it
          offers residents a truly rich urban experience.
        </Paragraph>
        <Paragraph>
          Sandwiched between some of the most beautiful forests, beaches, and
          mountains in the world, it’s no surprise that Vancouver is
          consistently ranked among the top five best places to live. The
          Greater Vancouver area, with a growing population of 2,463,431 (as of
          2016) also ranks in as the third-largest city in Canada, behind{' '}
          <LinkText href="/rental-listings/city/montreal">Montreal</LinkText>{' '}
          and <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink="https://liv.rent/blog/rental-resources/tips-for-renting-an-apartment-in-vancouver/"
        rentingTipsLinkZh="https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/"
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$2,000 - $2,600',
          bed2: '$3,000 - $3,500',
          bed3: '$4,500 - $4,900'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.vancouver:Apartment Hunting in Vancouver Paragraph">
            As the most expensive city in Canada, it will be hard to find{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/cheapest-neighbourhoods-for-rent-in-metro-vancouver/">
              cheap rentals in Vancouver
            </LinkText>{' '}
            - especially if you’re trying to find{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>
            . However, to start your search, you can refer to this guide on the{' '}
            <LinkText
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
                  : 'https://liv.rent/blog/neighbourhoods/cheapest-neighbourhoods-for-rent-in-metro-vancouver/'
              }
            >
              cheapest neighbourhoods for rent in Metro Vancouver
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Apartments for rent LOCATION 2 Paragraph">
              Apartments are the most common form of housing you’ll find in
              densely-populated {{ cityName }}. Renters should expect to see
              higher price tags for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in {{ cityName }}
              </LinkText>
              , especially compared to other Canadian provinces. This doesn’t
              mean that there aren’t bargains to be found though, but the cities
              proximity to nature and excellent weather makes it all worthwhile.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Houses for rent CITY Paragraph 2">
              There are plenty of{' '}
              <LinkText href={links.house}>
                houses for rent in {{ cityName }}
              </LinkText>{' '}
              for those with a higher budget, or who have multiple roommates. In
              more residential areas of the city, both partial houses and entire
              houses for rent are common – as well as both new builds and older
              homes from the city’s earlier days. {{ cityName }} is also known
              for an abundance of laneway homes and townhomes within the city,
              making it easy to find a place to rent that suits your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.vancouver:Condos for rent Vancouver Paragraph">
              In more urban areas like Downtown Vancouver,{' '}
              <LinkText href={links.apartment}>
                condos for rent in Vancouver
              </LinkText>{' '}
              are a common sight. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals CITY Paragraph">
              {{ cityName }} has plenty of work opportunities and world-class
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in {{ cityName }}
              </LinkText>{' '}
              are exceedingly popular, particularly during the summer months and
              at the beginning of school semesters. The added flexibility is an
              appealing prospect for many renters, and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION Paragraph">
              {{ cityName }} isn’t known for inexpensive rent, but if you’re
              careful in your search, it shouldn’t be too hard to find something
              within your budget. If you’re looking for{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
                    : links.under1000
                }
              >
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo.city.vancouver:Living in Vancouver Paragraph">
          <Paragraph>
            Vancouver is one of those cities that truly has something for
            everyone. Between the city’s mild temperatures, breathtaking natural
            setting, and urban convenience, it’s no wonder the city has such an
            active rental market.
          </Paragraph>
          <Paragraph>
            If you’re not sure that Vancouver is the right city for you, check
            out some of these resources to compare Vancouver with other Canadian
            cities.
          </Paragraph>
        </Trans>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E7%B2%BE%E5%93%81%E6%88%BF%E6%BA%90/2020%E7%89%88%E5%A4%A7%E6%B8%A9%E5%93%A5%E5%8D%8E1000%E6%88%96%E4%BB%A5%E4%B8%8B%E5%8F%AF%E4%BB%A5%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%88%BF%EF%BC%9F/">
              $1000加币在温哥华能租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/what-can-1800-in-rent-get-you-in-metro-vancouver/">
              What can $1800 in rent get you in Vancouver
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.vancouver:Popular Neighbourhoods Vancouver Paragraph">
          Vancouver is made up of smaller neighbourhoods with their own distinct
          features: Downtown,{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighbourhood-guide-west-end-vancouver/">
            West End
          </LinkText>
          , Strathcona, Grandview-Woodland, Hastings-Sunrise,
          Renfrew-Collingwood, Kensington-Cedar Cottage, Riley Park, South
          Cambie, Shaughnessy, Mount Pleasant, Fairview, Kitsilano, West Point
          Grey, UBC,{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighbourhood-guide-arbutus/">
            Arbutus-Ridge
          </LinkText>
          ,{' '}
          <LinkText
            href={
              isChinese
                ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e4%ba%a4%e9%80%9a%e3%80%81%e5%ad%a6%e6%a0%a1%e3%80%81%e7%be%8e%e9%a3%9f-%e8%af%a6%e8%a7%a3%e6%b8%a9%e8%a5%bf%e9%ab%98%e5%b0%9a%e5%90%8d%e5%8c%badunbar/'
                : 'https://liv.rent/blog/neighbourhoods/neighbourhood-guide-dunbar/'
            }
          >
            Dunbar-Southlands
          </LinkText>
          , Kerrisdale, Oakridge, Marpole, Sunset, Victoria-Fraserview, and
          Killarney.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.vancouver:Local Landmarks Vancouver Paragraph">
          While Vancouver is known for countless picturesque attractions, some
          of it’s most prominent landmarks include Stanley Park, Granville
          Island, Queen Elizabeth Park, Vancouver Art Gallery, Science World,
          English Bay, Kitsilano Beach, Victory Square, BC Place Stadium, Jack
          Poole Plaza, Vancouver Aquarium, and the Gastown Steam Clock.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Vancouver
